import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { useError } from '../contexts/ErrorContext';
import { vpnApi, VPNStatusResponse } from '../api/vpn';
import { useNavigate } from 'react-router-dom';
import AppLayout from '../components/AppLayout';
import LoadingSkeleton from '../components/LoadingSkeleton';
import OnboardingTour from '../components/OnboardingTour';
import HostingToggle from '../components/HostingToggle';
import {
  UserGroupIcon,
  ClipboardDocumentIcon,
  ServerIcon,
  UserIcon,
  WifiIcon
} from '@heroicons/react/24/outline';

// Desktop app types
interface ElectronAPI {
  getVPNStatus: () => Promise<DesktopVPNStatus>;
  getHostingStatus: () => Promise<HostingStatus>;
  startHosting: () => Promise<{ success: boolean; error?: string }>;
  stopHosting: () => Promise<{ success: boolean; error?: string }>;
  checkWireGuard: () => Promise<{ installed: boolean; instructions?: { method: string; command: string } }>;
}

interface DesktopVPNStatus {
  connected: boolean;
  status: string;
}

interface HostingStatus {
  hosting: boolean;
  connectedFriends?: ConnectedFriend[];
  serverConfig?: {
    endpoint: string;
    instructions?: {
      http: { settings: { proxy: string }; instructions: string[] };
      socks: { settings: { proxy: string } };
    };
  };
  shareInstructions?: {
    shareCode: string;
    invitationLink: string;
  };
}

interface ConnectedFriend {
  friendName?: string;
  ipAddress: string;
  protocol: string;
}

declare global {
  interface Window {
    electronAPI?: ElectronAPI;
  }
}

// Check if running in desktop app
const isDesktopApp = () => {
  return typeof window.electronAPI !== 'undefined';
};

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const { addError } = useError();
  const [vpnStatus, setVpnStatus] = useState<VPNStatusResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Desktop app states
  const [isHosting, setIsHosting] = useState(false);
  const [hostingStatus, setHostingStatus] = useState<HostingStatus | null>(null);
  const [connectedFriends, setConnectedFriends] = useState<ConnectedFriend[]>([]);
  const fetchVpnStatus = useCallback(async () => {
    try {
      const response = await vpnApi.getStatus();
      setVpnStatus(response.data);
    } catch (err) {
      addError('Failed to fetch VPN status');
    } finally {
      setIsLoading(false);
    }
  }, [addError]);

  const fetchHostingStatus = useCallback(async () => {
    if (!isDesktopApp() || !window.electronAPI) return;

    try {
      const status = await window.electronAPI.getHostingStatus();
      setHostingStatus(status);
      setIsHosting(status.hosting);
      setConnectedFriends(status.connectedFriends || []);
    } catch (error) {
      console.error('Failed to fetch hosting status:', error);
    }
  }, []);

  useEffect(() => {
    fetchVpnStatus();

    if (isDesktopApp()) {
      fetchHostingStatus();
    }
  }, [fetchHostingStatus, fetchVpnStatus]);

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  // Desktop app functions
  const startHosting = useCallback(async () => {
    if (!isDesktopApp() || !window.electronAPI) return;

    try {
      setIsLoading(true);
      const result = await window.electronAPI.startHosting();

      if (result.success) {
        setIsHosting(true);
        await fetchHostingStatus();
        toast.success('VPN hosting started successfully');
      } else {
        const errorMsg = result.error || 'Failed to start hosting';
        toast.error('Failed to start hosting', errorMsg);
      }
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Failed to start hosting';
      toast.error('Failed to start hosting', errorMsg);
    } finally {
      setIsLoading(false);
    }
  }, [fetchHostingStatus, toast]);

  const stopHosting = async () => {
    if (!isDesktopApp() || !window.electronAPI) return;

    try {
      setIsLoading(true);
      const result = await window.electronAPI.stopHosting();

      if (result.success) {
        setIsHosting(false);
        setHostingStatus(null);
        setConnectedFriends([]);
        toast.success('VPN hosting stopped');
      } else {
        const errorMsg = result.error || 'Failed to stop hosting';
        toast.error('Failed to stop hosting', errorMsg);
      }
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Failed to stop hosting';
      toast.error('Failed to stop hosting', errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Unified handler for toggling VPN hosting on/off.
   *
   * This function provides a single interface for managing VPN sharing across both
   * desktop (Electron) and web platforms, routing to the appropriate APIs based on
   * the runtime environment.
   *
   * **Desktop Flow (Electron)**:
   * - Uses window.electronAPI.startHosting() / stopHosting()
   * - Manages state through fetchHostingStatus()
   *
   * **Web Flow (Browser)**:
   * - Uses vpnApi.enableSharing() / disableSharing()
   * - Refreshes VPN status after operations
   * - Displays toast notifications for user feedback
   *
   * **Error Handling**:
   * - Catches and displays user-friendly error messages via toast
   * - Maintains loading state during async operations
   *
   * @async
   */
  const handleToggleHosting = async () => {
    if (isDesktopApp()) {
      // Desktop app uses Electron API
      if (isHosting) {
        await stopHosting();
      } else {
        await startHosting();
      }
    } else {
      // Web app uses API
      try {
        setIsLoading(true);
        if (vpnStatus?.sharing_enabled) {
          // Disable sharing
          await vpnApi.disableSharing();
          toast.success('VPN sharing disabled');
        } else {
          // Enable sharing
          await vpnApi.enableSharing({});
          toast.success('VPN sharing enabled');
        }
        // Refresh status
        await fetchVpnStatus();
      } catch (error) {
        const errorMsg = error instanceof Error ? error.message : 'Failed to toggle sharing';
        toast.error('Failed to toggle VPN sharing', errorMsg);
      } finally {
        setIsLoading(false);
      }
    }
  };

  if (isLoading) {
    return (
      <AppLayout>
        <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
          <LoadingSkeleton type="card" count={3} />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <OnboardingTour />
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        {/* VPN Hosting Section - Unified for all platforms */}
        <div className="bg-white shadow rounded-lg mb-8">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  VPN Sharing
                </h3>
                <p className="text-sm text-gray-500">
                  {isDesktopApp()
                    ? 'Host a secure WireGuard VPN server with direct P2P connections'
                    : 'Share your network connection with friends through our relay service'
                  }
                </p>
              </div>
              <div className="flex items-center space-x-2">
                {(isDesktopApp() ? isHosting : vpnStatus?.sharing_enabled) ? (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    <ServerIcon className="h-4 w-4 mr-1" />
                    Active
                  </span>
                ) : (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                    Inactive
                  </span>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="text-sm font-medium text-gray-500">Status</div>
                <div className="text-lg font-semibold text-gray-900">
                  {isDesktopApp()
                    ? (isHosting ? 'Hosting' : 'Not Hosting')
                    : (vpnStatus?.sharing_enabled ? 'Sharing' : 'Not Sharing')
                  }
                </div>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="text-sm font-medium text-gray-500">Active Connections</div>
                <div className="text-lg font-semibold text-gray-900">
                  {isDesktopApp()
                    ? connectedFriends.length
                    : (vpnStatus?.active_connections || 0)
                  }
                </div>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="text-sm font-medium text-gray-500">
                  {isDesktopApp() ? 'Server IP' : 'VPN IP'}
                </div>
                <div className="text-lg font-semibold text-gray-900">
                  {isDesktopApp()
                    ? (hostingStatus?.serverConfig?.endpoint || 'Not available')
                    : (vpnStatus?.vpn_ip || (
                        <span className="text-sm text-gray-600">
                          {vpnStatus?.sharing_enabled ? 'Generating...' : 'Enable sharing to get IP'}
                        </span>
                      ))
                  }
                </div>
              </div>
            </div>

            {/* Device-Level VPN Hosting Status (Web App) */}
            {!isDesktopApp() && vpnStatus?.devices && vpnStatus.devices.length > 0 && (
              <div className="mb-4">
                <h4 className="text-sm font-medium text-gray-900 mb-3">Device VPN Hosting</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {vpnStatus.devices.map((device) => (
                    <div key={device.device_id} className="border border-gray-200 rounded-lg p-3 space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <ServerIcon className="h-4 w-4 text-purple-500" />
                          <span className="text-sm font-medium text-gray-900">{device.device_name}</span>
                        </div>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          device.is_hosting_enabled
                            ? 'bg-green-100 text-green-700'
                            : 'bg-gray-100 text-gray-700'
                        }`}>
                          {device.is_hosting_enabled ? 'Hosting' : 'Inactive'}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
                        <div>
                          <span className="font-medium">VPN IP:</span>{' '}
                          <span className="text-gray-900">{device.vpn_ip || 'Not assigned'}</span>
                        </div>
                        <div>
                          <span className="font-medium">Connections:</span>{' '}
                          <span className="text-gray-900">{device.active_connections}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-3 text-xs text-gray-500">
                  Manage device hosting in the{' '}
                  <button
                    onClick={() => navigate('/devices')}
                    className="text-primary-600 hover:text-primary-700 font-medium"
                  >
                    Devices page
                  </button>
                </div>
              </div>
            )}

            {/* Desktop: Connection Instructions */}
            {isDesktopApp() && isHosting && hostingStatus?.shareInstructions && (
              <div className="mb-4">
                <h4 className="text-sm font-medium text-gray-900 mb-2">Share Your VPN Connection:</h4>
                <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                  <div className="text-xs">
                    <strong>Share Code:</strong> <code className="bg-gray-200 px-1 rounded">{hostingStatus.shareInstructions.shareCode}</code>
                  </div>
                  <div className="text-xs">
                    <strong>Invitation Link:</strong> <code className="bg-gray-200 px-1 rounded text-xs break-all">{hostingStatus.shareInstructions.invitationLink}</code>
                  </div>
                  <div className="text-xs text-green-700 font-medium">
                    ✅ Direct P2P Connection - Zero Server Costs
                  </div>
                </div>
              </div>
            )}

            {/* Desktop: Connected Friends */}
            {isDesktopApp() && connectedFriends.length > 0 && (
              <div className="mb-4">
                <h4 className="text-sm font-medium text-gray-900 mb-2">Connected Friends:</h4>
                <div className="space-y-2">
                  {connectedFriends.map((friend, index) => (
                    <div key={index} className="flex items-center justify-between bg-gray-50 rounded-lg p-2">
                      <div className="flex items-center">
                        <UserIcon className="h-4 w-4 text-gray-400 mr-2" />
                        <span className="text-sm text-gray-900">{friend.friendName || `Friend ${index + 1}`}</span>
                      </div>
                      <div className="text-xs text-gray-500">
                        {friend.ipAddress} • {friend.protocol}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Web: Guidance for enabling sharing */}
            {!isDesktopApp() && !vpnStatus?.sharing_enabled && (
              <div className="mb-4 bg-gradient-to-r from-primary-50 to-purple-50 border border-primary-200 rounded-lg p-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <ServerIcon className="h-6 w-6 text-primary-600" />
                  </div>
                  <div className="ml-3 flex-1">
                    <h4 className="text-sm font-medium text-primary-900 mb-1">
                      Start Sharing Your Connection
                    </h4>
                    <p className="text-sm text-primary-700 mb-3">
                      Enable VPN sharing to let friends connect through your network. Perfect for gaming, streaming, or secure browsing.
                    </p>
                    <button
                      onClick={() => navigate('/connections')}
                      className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 transition-colors duration-200"
                    >
                      <WifiIcon className="h-4 w-4 mr-1" />
                      Enable Sharing
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Hosting Toggle - Issue #265 */}
            <HostingToggle
              isActive={isDesktopApp() ? isHosting : (vpnStatus?.sharing_enabled || false)}
              isLoading={isLoading}
              onToggle={handleToggleHosting}
              connectionCount={
                isDesktopApp()
                  ? connectedFriends.length
                  : (vpnStatus?.active_connections || 0)
              }
            />
          </div>
        </div>


        {/* Friend Code Section */}
        {user?.friend_code && (
          <div className="bg-white shadow rounded-lg mb-8">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                Your Friend Code
              </h3>
              <div className="flex items-center space-x-3">
                <div className="flex-1">
                  <div className="relative">
                    <input
                      type="text"
                      readOnly
                      value={user.friend_code}
                      className="block w-full pr-10 border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500 sm:text-sm bg-gray-50"
                    />
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                      <button
                        onClick={() => copyToClipboard(user.friend_code!)}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        <ClipboardDocumentIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              <p className="mt-2 text-sm text-gray-500">
                Share this code with friends to let them request VPN access
              </p>
            </div>
          </div>
        )}

        {/* Quick Actions - Unified navigation for all platforms */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
              Quick Actions
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <button
                onClick={() => navigate('/connections')}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                <WifiIcon className="h-4 w-4 mr-2" />
                Manage Connections
              </button>
              <button
                onClick={() => navigate('/friends')}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                <UserGroupIcon className="h-4 w-4 mr-2" />
                Manage Friends
              </button>
              <button
                onClick={() => navigate('/usage')}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                View Usage Stats
              </button>
              <button
                onClick={() => navigate('/profile')}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                <UserIcon className="h-4 w-4 mr-2" />
                Profile & Settings
              </button>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
};

export default Dashboard;
